// La page de résultats de la barre de recherche.
//
// Deux recherches très différentes, côte à côte, et c'est voulu :
//   · les MEMBRES sont demandés à la base, puis classés par pertinence
//     (un ami ou un frère de la même église passe devant un homonyme inconnu) ;
//   · les PUBLICATIONS sont filtrées dans ce qu'on a déjà — chercher du texte
//     dans une base de documents coûterait un service d'indexation, et la
//     communauté n'est pas encore assez grande pour le justifier.
//
// L'adresse n'est PAS mise à jour pendant la frappe : cela relancerait l'écran
// entier à chaque lettre.

import { useEffect, useMemo, useRef, useState } from "react";
import Store from "../../store.js";
import { classerRecherche } from "../../algorithmes.js";
import Avatar from "../Avatar.jsx";
import Certifie from "../Certifie.jsx";
import Vide, { Chargement } from "../Vide.jsx";
import { ListePublicationsFournie } from "../ListePublications.jsx";
import { useAbonnement, useSignaux } from "../hooks.js";

// Les marques diacritiques sont écrites en échappement : un intervalle de
// caractères combinants collé tel quel dans un fichier se lit mal et se casse
// au premier copier-coller.
const sansAccents = (s) =>
  (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

function Membres({ resultats, requete }) {
  if (resultats === null) return <Chargement />;
  if (!resultats.length)
    return (
      <p className="texte-doux petit" style={{ padding: "0 4px" }}>
        Aucun membre pour « {requete} ».
      </p>
    );

  return (
    <div className="grille-membres">
      {resultats.map((u) => (
        <a className="membre" href={`#/profil/${u.uid}`} key={u.uid}>
          <span className="membre__qui">
            <Avatar membre={u} taille={46} />
            <span>
              <strong>
                {u.nom}
                <Certifie membre={u} taille={13} />
              </strong>
              <span className="petit texte-doux">
                {[u.eglise, u.ville].filter(Boolean).join(" · ") || "Membre"}
              </span>
            </span>
          </span>
        </a>
      ))}
    </div>
  );
}

export default function Recherche({ ctx, params }) {
  const depart = decodeURIComponent(params.q || "");
  const [requete, setRequete] = useState(depart);
  const [membres, setMembres] = useState(depart ? null : []);
  const champRef = useRef(null);
  const versionSignaux = useSignaux(ctx.signaux);

  const tous = useAbonnement((cb) => Store.posts.subscribe(cb), []);

  // Venu sans rien chercher : le curseur se met dans le champ. Venu avec une
  // requête, on ne le vole pas — la personne veut lire ses résultats.
  useEffect(() => {
    if (!depart) champRef.current?.focus();
  }, [depart]);

  useEffect(() => {
    const texte = requete.trim();
    if (!texte) {
      setMembres([]);
      return;
    }
    let vivant = true;
    const t = setTimeout(async () => {
      const trouves = (await Store.users.search(texte)).filter((u) => u.uid !== ctx.moi.uid);
      if (!vivant) return;
      ctx.signaux?.apprendreMembres(trouves);
      const classes = ctx.signaux
        ? classerRecherche(trouves, texte, ctx.signaux.instantane())
        : trouves;
      setMembres(classes.slice(0, 12));
    }, 220);
    return () => {
      vivant = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requete, versionSignaux, ctx]);

  const posts = useMemo(() => {
    const texte = sansAccents(requete);
    if (!texte) return [];
    return tous.filter(
      (p) => sansAccents(p.texte).includes(texte) || sansAccents(p.auteurNom).includes(texte)
    );
  }, [tous, requete]);

  const rien = !requete.trim();

  return (
    <div className="colonne">
      <div className="carte">
        <h1 className="titre-page">Recherche</h1>
        <input
          className="champ"
          type="search"
          ref={champRef}
          value={requete}
          onChange={(e) => setRequete(e.target.value)}
          placeholder="Rechercher un membre, une publication…"
          autoComplete="off"
        />
      </div>

      <h2 className="titre-section">Membres</h2>
      {rien ? (
        <Vide
          symbole="🔍"
          titre="Tapez quelque chose"
          texte="Nom, ville, église, ou mot d'une publication."
        />
      ) : (
        <Membres resultats={membres} requete={requete.trim()} />
      )}

      <h2 className="titre-section">Publications</h2>
      <ListePublicationsFournie
        ctx={ctx}
        posts={posts}
        vide={["🔍", "Aucune publication", "Aucun texte ne correspond à cette recherche."]}
      />
    </div>
  );
}
